import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Select, Store } from '@ngxs/store';
import { Observable, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { AccountInfo } from 'src/app/shared/models/accountInfo.model';
import { BuildingFilterModel } from 'src/app/shared/models/building-filter.model';
import { BuildingModel } from 'src/app/shared/models/building.model';
import { BuildingCategoryModel } from 'src/app/shared/models/complex-building-category.model';
import { MunicipalityModel } from 'src/app/shared/models/municipality.model';
import { PeriodModel } from 'src/app/shared/models/period.model';
import { SearchStreet } from 'src/app/shared/models/search-street.model';
import {
  GetAccountFromCache,
  SetTitle,
} from 'src/app/shared/ngxs/action/account.action';
import { FilterBuildings } from 'src/app/shared/ngxs/action/building.action';
import { FilterPeriods } from 'src/app/shared/ngxs/action/period.action';
import { AccountState } from 'src/app/shared/ngxs/state/account.state';
import { MunicipalityState } from 'src/app/shared/ngxs/state/municipality.state';
import { PeriodState } from 'src/app/shared/ngxs/state/period.state';
import { FilterService } from 'src/app/shared/services/filter.service';
import { LocalizationService } from 'src/app/shared/services/localization.service';
import { ImportBuildingModel } from '../../shared/models/import-buildings.model';
import { ExportImportBuildingsComponent } from '../export-import-buildings/export-import-buildings.component';
import { ManageBuildingComponent } from '../manage-building/manage-building.component';
import { ConfirmationDialogComponent } from './../../shared/components/confirmation-dialog/confirmation-dialog.component';
import {
  BuildingManagementType,
  BuildingsActionType,
  LanguageType,
  ManageBuildingTypes,
} from './../../shared/models/enum.model';
import {
  ClearBuildings,
  GetBuilding,
  GetBuildingCategories,
  RemoveBuilding,
  SearchByStreet,
} from './../../shared/ngxs/action/building.action';
import { FilterMunicipalitiesWithoutFiltering } from './../../shared/ngxs/action/municipality.action';
import { BuildingState } from './../../shared/ngxs/state/building.state';

@Component({
  selector: 'app-buildings',
  templateUrl: './buildings.component.html',
  styleUrls: ['./buildings.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition(
        'expanded <=> collapsed',
        animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')
      ),
    ]),
  ],
  providers: [FilterService],
})
export class BuildingsComponent implements OnInit, OnDestroy {
  subscription = new Subscription();

  municipalityForm = new FormControl();
  periodForm = new FormControl();
  streetForm = new FormControl();
  managementForm = new FormControl();
  currentFilter: BuildingFilterModel;
  searchLatitude = 0;
  searchLongitude = 0;
  selectedAllMunicipalities = false;
  selectedAllPeriods = false;
  municipalities: MunicipalityModel[];
  periods: PeriodModel[];
  streets: string[] = [];
  defaultGuid = '00000000-0000-0000-0000-000000000000';
  buildingManagementTypes = BuildingManagementType;

  buildingManagementTypeEnumValues = Object.values(
    BuildingManagementType
  ).filter((type) => isNaN(<any>type) && type !== 'values');

  currentAccount: AccountInfo = undefined;

  @ViewChild('paginator')
  paginator: MatPaginator;

  @Select(BuildingState.buildings)
  buildings$: Observable<BuildingModel[]>;

  @Select(BuildingState.buildingsTotal)
  buildingsTotal$: Observable<number>;

  @Select(PeriodState.periods)
  periods$: Observable<PeriodModel[]>;

  @Select(MunicipalityState.municipalities)
  municipalities$: Observable<MunicipalityModel[]>;

  @Select(BuildingState.streetRecommendations)
  streetRecommendations$: Observable<string[]>;

  @Select(AccountState.account)
  account$: Observable<AccountInfo>;

  @Select(BuildingState.categories)
  categories$: Observable<BuildingCategoryModel[]>;

  isAdministrator = false;
  categories = [];
  validCategories = [];

  @Select(BuildingState.filter)
  filter$: Observable<BuildingFilterModel>;

  streetRecommendations = [];

  filteredStreets: Observable<string[]>;
  language = '';
  model: ImportBuildingModel;
  manageBuildingTypes = ManageBuildingTypes;
  columnsToDisplay: string[] = [
    'expand',
    'street',
    'construction',
    'municipality',
    'city',
    'actions',
  ];
  dataSource = new MatTableDataSource<BuildingModel>();
  expandedElement: BuildingModel | null;
  pageSize = 5;
  pageSizeOptions = [5, 10, 25, 100];
  total = 0;

  constructor(
    private dialog: MatDialog,
    private store: Store,
    private router: Router,
    private translateService: TranslateService,
    private filterService: FilterService,
    private localizationService: LocalizationService
  ) {
    this.model = new ImportBuildingModel();
    this.translateService.get('buildings').subscribe((x) => {
      this.store.dispatch(new SetTitle(x));
    });

    this.language = this.translateService.currentLang;

    this.translateLabels();
  }

  ngOnInit(): void {
    this.subscription.add(
      this.translateService.onLangChange.subscribe((x) => {
        if (x) {
          this.language = this.translateService.currentLang;
          this.store.dispatch(new FilterPeriods());
          this.store.dispatch(
            new FilterMunicipalitiesWithoutFiltering(
              this.translateService.currentLang
            )
          );

          this.translateLabels();
        }
      })
    );

    this.store.dispatch(new GetAccountFromCache());
    this.store.dispatch(
      new FilterMunicipalitiesWithoutFiltering(
        this.translateService.currentLang
      )
    );
    this.store.dispatch(new FilterPeriods());
    this.store.dispatch(new GetBuildingCategories());

    this.dataSource.paginator = this.paginator;
    this.store.dispatch(
      new FilterBuildings(
        this.filterService.generateHttpParams(),
        new BuildingFilterModel(
          [],
          [],
          '',
          '',
          [],
          0,
          0,
          LanguageType[this.translateService.currentLang.toUpperCase()]
        )
      )
    );

    this.subscribeEvents();
  }

  subscribeEvents() {
    this.subscription.add(
      this.categories$.subscribe((x) => {
        if (x) {
          this.categories = x;

          this.validCategories = this.getValidCategories();
        }
      })
    );

    this.subscription.add(
      this.buildings$.subscribe((x) => {
        this.dataSource = new MatTableDataSource<BuildingModel>(x);
      })
    );

    this.subscription.add(
      this.buildingsTotal$.subscribe((x) => {
        if (x) {
          this.total = x;
        }
      })
    );

    this.subscription.add(
      this.periods$.subscribe((x) => {
        if (x) {
          this.periods = x;
        }
      })
    );

    this.subscription.add(
      this.municipalities$.subscribe((x) => {
        if (x) {
          this.municipalities = x;
        }
      })
    );

    this.subscription.add(
      this.streetForm.valueChanges
        .pipe(debounceTime(500))
        .subscribe((newValue) => {
          if (newValue && newValue.length > 0) {
            const searchStreet = new SearchStreet();
            searchStreet.streetName = newValue;
            searchStreet.languageType =
              LanguageType[this.translateService.currentLang.toUpperCase()];
            this.store.dispatch(new SearchByStreet(searchStreet));
          } else {
            this.streetRecommendations = [];
          }
          this.filterBuildings();
        })
    );

    this.subscription.add(
      this.streetRecommendations$.subscribe((x) => {
        if (x) {
          this.streetRecommendations = x;
        }
      })
    );

    this.subscription.add(
      this.account$.subscribe((x) => {
        if (x) {
          this.currentAccount = x;
          if (x.userType.toString() === 'Administrator') {
            this.isAdministrator = true;
          }
        }
      })
    );

    this.subscription.add(
      this.filter$.subscribe((x) => {
        if (x) {
          this.currentFilter = x;
        }
      })
    );
  }

  onExpand(element: BuildingModel) {
    if (this.expandedElement) {
      this.store.dispatch(new GetBuilding(this.expandedElement.id));
    }
  }

  getValidCategories() {
    let valid = [];

    const buildingCategories = this.categories.map((x) => x.name);

    if (this.isAdministrator) {
      return buildingCategories;
    }

    if (!this.isAdministrator && this.currentAccount) {
      valid = [];

      this.currentAccount?.categories?.forEach((categoryId) => {
        valid.push(this.categories.filter((x) => x.id == categoryId)[0]?.name);
      });
    }

    return valid;
  }

  noManagementTranslate = '';
  companyManagementTranslate = '';
  tenantsCommunityTranslate = '';

  translateLabels() {
    this.noManagementTranslate =
      this.localizationService.translate('no-management');
    this.companyManagementTranslate =
      this.localizationService.translate('company-manager');
    this.tenantsCommunityTranslate =
      this.localizationService.translate('tenants-community');
  }

  getTypeDescription(type: BuildingManagementType) {
    if (type === BuildingManagementType.NoManagement) {
      return this.noManagementTranslate;
    } else if (type === BuildingManagementType.SingleManagement) {
      return this.companyManagementTranslate;
    } else if (type === BuildingManagementType.GroupOfOwners) {
      return this.tenantsCommunityTranslate;
    }
  }

  onSelectedMunicipalities(event: any) {
    this.filterBuildings();
  }

  onSelectedManagement(event: any) {
    this.filterBuildings();
  }

  onSelectedPeriods(event: any) {
    this.filterBuildings();
  }

  getYear(data: string) {
    return new Date(data).getFullYear();
  }

  filterBuildings() {
    const municipalities = this.municipalityForm.value;
    const periods = this.periodForm.value;
    const street = this.streetForm.value;
    const streetNumber = '';
    const types = this.managementForm.value;
    const language =
      LanguageType[this.translateService.currentLang.toUpperCase()];

    const filterModel = new BuildingFilterModel(
      municipalities,
      periods,
      street,
      streetNumber,
      types,
      this.searchLatitude,
      this.searchLongitude,
      language
    );
    this.store.dispatch(
      new FilterBuildings(this.filterService.generateHttpParams(), filterModel)
    );
  }

  addressChange(address: any) {
    if (address) {
      this.searchLongitude = address.geometry.location.lng();
      this.searchLatitude = address.geometry.location.lat();
      this.streetForm.setValue(address.formatted_address);
    } else {
      this.resetCoordinates();
    }

    this.filterBuildings();
  }

  resetCoordinates() {
    this.searchLongitude = 0;
    this.searchLatitude = 0;
  }

  deleteBuilding(event: any, element: BuildingModel) {
    event.stopPropagation();

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'Delete Building',
        text: 'Are you sure you want to delete this building?',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.store.dispatch(new RemoveBuilding(element.id));
      }
    });
  }

  addBuilding() {
    const dialogRef = this.dialog.open(ManageBuildingComponent, {
      width: '85%',
      data: {
        type: ManageBuildingTypes.Add,
      },
    });
  }

  exportBuildings() {
    const dialogRef = this.dialog.open(ExportImportBuildingsComponent, {
      width: '35%',
      data: {
        model: this.model,
        action: BuildingsActionType.Export,
      },
    });
  }

  editBuilding(event: any, model: BuildingModel) {
    event.stopPropagation();

    const dialogRef = this.dialog.open(ManageBuildingComponent, {
      width: '85%',
      data: {
        modelId: model.id,
        type: ManageBuildingTypes.Update,
      },
    });
  }

  mapPreview(event: any) {
    event.stopPropagation();
  }

  setAcceptFiles() {
    return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  }

  onFileChange(event: any) {
    if (event.target.files && event.target.files.length) {
      const reader = new FileReader();
      const [file] = event.target.files;
      reader.onload = () => {
        const base64Data = reader.result.toString();
        this.model.extension = file.name.split('.').pop();
        this.model.size = file.size;
        this.model.name = file.name;
        this.model.fileAsBase64 = base64Data;

        const dialogRef = this.dialog.open(ExportImportBuildingsComponent, {
          width: '35%',
          data: {
            model: this.model,
            action: BuildingsActionType.Import,
          },
        });

        dialogRef.afterClosed().subscribe((result) => {
          if (result) {
            event.target.value = '';
          }
        });
      };
      // Read the file
      reader.readAsDataURL(file);
    }
  }

  goToDetails(element: BuildingModel) {
    this.router.navigate([`/building/${element.id}/preview`]);
  }

  // changePage(event: PageEvent) {
  //   this.filterService.page = event;

  //   this.store.dispatch(
  //     new FilterBuildings(this.filterService.generateHttpParams())
  //   );
  // }
  changePage(event: PageEvent) {
    this.filterService.page = event;

    this.store.dispatch(
      new FilterBuildings(
        this.filterService.generateHttpParams(),
        this.currentFilter ? this.currentFilter : undefined
      )
    );
  }

  clearAll() {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'Clear Buildings',
        text: 'Are you sure you want to delete all buildings?',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.store.dispatch(new ClearBuildings());
      }
    });
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  selectAllMunicipalities(all: boolean) {
    this.selectedAllMunicipalities = all;
    if (all) {
      const array = this.municipalities.map((x) => x.id);
      array.push(this.defaultGuid);
      this.municipalityForm.setValue(array);
    } else {
      this.municipalityForm.setValue([]);
    }

    this.filterBuildings();
  }

  selectAllPeriods(all: boolean) {
    this.selectedAllPeriods = all;
    if (all) {
      const array = this.periods.map((x) => x.id);
      array.push(this.defaultGuid);
      this.periodForm.setValue(array);
    } else {
      this.periodForm.setValue([]);
    }

    this.filterBuildings();
  }
}
